import React, { useState, useEffect } from "react";
import Papa, { Parser } from "papaparse";
import * as XLSX from "xlsx";
import { Spreadsheet } from "react-spreadsheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCompress,
  faExclamationTriangle,
  faExpand,
  faFile,
  faFileAlt,
  faFileCsv,
  faFileExcel,
  faTable,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { colors } from "../../global";
import { toast } from "react-toastify";

const FileToSpreadsheetModalSelect = ({
  file,
  closeModal,
  metadata,
  onSubmit,
  selectionType,
  sheet_name = null,
}) => {
  const [data, setData] = useState([]);
  const [fileType, setFileType] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [columnWidths, setColumnWidths] = useState([]);

  const calculateColumnWidths = (data) => {
    if (!data || data.length === 0) return [];

    const maxCols = Math.max(...data.map((row) => row.length));
    const widths = [];

    for (let col = 0; col < maxCols; col++) {
      let maxWidth = 80;

      for (let row = 0; row < Math.min(data.length, 100); row++) {
        const cellValue = data[row][col]?.value || "";
        const cellLength = String(cellValue).length;

        const estimatedWidth = Math.min(Math.max(cellLength * 8 + 24, 80), 300);
        maxWidth = Math.max(maxWidth, estimatedWidth);
      }

      widths.push(maxWidth);
    }

    return widths;
  };

  const handleCSVFileUpload = (file) => {
    setIsLoading(true);
    const MAX_ROWS = 100;
    const tempData = [];

    Papa.parse(file, {
      worker: true,
      step: (row) => {
        const formattedRow = row.data.map((cell) => ({ value: cell }));
        tempData.push(formattedRow);

        if (tempData.length >= MAX_ROWS) {
          // Abort parsing further
          Parser.abort();
          setData(tempData);
          const widths = calculateColumnWidths(tempData);
          setColumnWidths(widths);
          setIsLoading(false);
        }
      },
      complete: () => {
        // If file had fewer than 100 rows
        if (tempData.length < MAX_ROWS) {
          setData(tempData);
          const widths = calculateColumnWidths(tempData);
          setColumnWidths(widths);
          setIsLoading(false);
        }
      },
      error: (err) => {
        toast.error("Failed to parse CSV");
        setIsLoading(false);
      },
    });
  };

  const handleExcelFileUpload = (file, sheet_name) => {
    setIsLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const ab = e.target.result;
      const wb = XLSX.read(ab, { type: "array" });
      setFileType("Excel");
      const ws =
        wb.Sheets[sheet_name ?? metadata?.sheet_information?.sheet_name];

      const rawData = XLSX.utils
        .sheet_to_json(ws, {
          header: 1,
          blankrows: true,
          defval: "",
        })
        .slice(0, 100);

      const maxCols = Math.max(...rawData.map((row) => row.length));
      const normalizedData = rawData.map((row) => {
        const filledRow = [...row];
        while (filledRow.length < maxCols) {
          filledRow.push("");
        }
        return filledRow.map((cell) => ({ value: cell }));
      });

      setData(normalizedData.slice(0, 200));
      setTimeout(() => {
        const widths = calculateColumnWidths(normalizedData);
        setColumnWidths(widths);
      }, 100);

      setIsLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (file) {
      if (file.name.endsWith(".csv")) {
        handleCSVFileUpload(file);
      } else if (
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xlsb")
      ) {
        handleExcelFileUpload(file, sheet_name);
      } else {
        toast.error("Invalid file type. Please upload a CSV or Excel file.");
      }
    }
  }, [file]);

  const getFileIcon = () => {
    if (fileType === "CSV") {
      return faFileCsv;
    } else if (fileType === "Excel") {
      return faFileExcel;
    }
    return faFile;
  };

  function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }
  const getSelectedLabel = (cells) => {
    if (cells.length === 0) return "";

    const sorted = [...cells].sort((a, b) =>
      a.row !== b.row ? a.row - b.row : a.col - b.col
    );

    const rows = new Set(sorted.map((c) => c.row));
    const cols = new Set(sorted.map((c) => c.col));
    const rowArr = Array.from(rows).sort((a, b) => a - b);
    const colArr = Array.from(cols).sort((a, b) => a - b);

    const toColLetter = (col) => {
      let letter = "";
      while (col >= 0) {
        letter = String.fromCharCode((col % 26) + 65) + letter;
        col = Math.floor(col / 26) - 1;
      }
      return letter;
    };

    const isFullColumn = rowArr[0] === -1;
    const isFullRow = colArr[0] === -1;

    if (isFullColumn) {
      console.log(colArr);
      var uniqueColumn = colArr.filter(onlyUnique);
      if (uniqueColumn.length === 1) return toColLetter(colArr[0]);
      return `${toColLetter(uniqueColumn[0])}:${toColLetter(
        uniqueColumn[uniqueColumn.length - 1]
      )}`;
    }

    if (isFullRow) {
      var uniqueRow = rowArr.filter(onlyUnique);
      if (uniqueRow.length === 1) return uniqueRow[0] + 1;
      return `${uniqueRow[0] + 1}:${uniqueRow[uniqueRow.length - 1] + 1}`;
    }

    if (cells.length === 1) {
      const cell = sorted[0];
      return `${cell.row + 1}${toColLetter(cell.col)}`;
    }

    if (cols.size === 1) {
      return `${rowArr[0] + 1}${toColLetter(colArr[0])}:${
        rowArr[rowArr.length - 1] + 1
      }${toColLetter(colArr[0])}`;
    }

    if (rows.size === 1) {
      return `${rowArr[0] + 1}${toColLetter(colArr[0])}:${
        rowArr[0] + 1
      }${toColLetter(colArr[colArr.length - 1])}`;
    }

    return `${rowArr[0] + 1}${toColLetter(colArr[0])}:${
      rowArr[rowArr.length - 1] + 1
    }${toColLetter(colArr[colArr.length - 1])}`;
  };

  const getExcelColumnLabel = (colIndex) => {
    let label = "";
    let index = colIndex + 1;
    while (index > 0) {
      let remainder = (index - 1) % 26;
      label = String.fromCharCode(65 + remainder) + label;
      index = Math.floor((index - 1) / 26);
    }
    return label;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div
        className={`bg-white shadow-2xl transition-all duration-300 flex flex-col
      ${
        isFullScreen
          ? "w-full h-full rounded-none m-0"
          : "w-full max-w-6xl max-h-[90vh] rounded-lg"
      }`}
        style={{ backgroundColor: colors.white }}
      >
        {/* Modal Header */}
        <div
          className="flex justify-between items-center p-6 border-b-2 rounded-t-lg"
          style={{
            backgroundColor: colors.primary,
            borderColor: colors.secondary,
          }}
        >
          <div className="flex items-center space-x-3 min-w-0">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
              style={{ backgroundColor: colors.accent }}
            >
              <FontAwesomeIcon
                icon={getFileIcon()}
                className="text-white text-lg"
              />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-white truncate">
                File Preview
              </h2>
              {file && (
                <p className="text-sm opacity-90 text-white truncate">
                  {file.name}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all duration-200 font-medium"
              type="button"
            >
              <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
              <span className="hidden sm:inline">
                {isFullScreen ? "Exit Full Screen" : "Full Screen"}
              </span>
            </button>
            <button
              onClick={closeModal}
              className="flex items-center justify-center w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all duration-200"
              aria-label="Close"
              type="button"
            >
              <FontAwesomeIcon icon={faX} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div
          className="flex-1 overflow-auto p-6"
          style={{
            backgroundColor: colors.white,
          }}
        >
          {fileType && (
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium"
                style={{ backgroundColor: colors.secondary }}
              >
                <FontAwesomeIcon icon={getFileIcon()} />
                <span className="whitespace-nowrap">{fileType} File</span>
              </div>

              {data.length > 0 && (
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium"
                  style={{ backgroundColor: colors.accent }}
                >
                  <FontAwesomeIcon icon={faTable} />
                  <span className="whitespace-nowrap">
                    Showing first {data.length} rows x {data[0]?.length || 0}{" "}
                    columns
                  </span>
                </div>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div className="relative">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-opacity-20"
                  style={{
                    borderColor: colors.primary,
                    borderTopColor: colors.accent,
                  }}
                ></div>
                <FontAwesomeIcon
                  icon={faFileAlt}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg"
                />
              </div>
              <div className="text-center">
                <p
                  className="text-lg font-medium"
                  style={{ color: colors.primary }}
                >
                  Processing file...
                </p>
                <p className="text-sm mt-1" style={{ color: colors.darkGray }}>
                  Please wait while we parse your data
                </p>
              </div>
            </div>
          ) : data.length > 0 ? (
            <div
              className="border-2 rounded-lg overflow-hidden shadow-inner"
              style={{
                borderColor: colors.gray,
                height: isFullScreen ? "calc(100vh - 240px)" : "500px",
              }}
            >
              <div className="h-full overflow-auto bg-white relative">
                <div
                  className="spreadsheet-wrapper h-full w-full"
                  style={{
                    "--column-widths": columnWidths
                      .map((w) => `${w}px`)
                      .join(" "),
                  }}
                >
                  <Spreadsheet
                    data={data}
                    columnLabels={Array.from(
                      { length: Math.max(...data.map((row) => row.length)) },
                      (_, i) => getExcelColumnLabel(i)
                    )}
                    rowLabels={Array.from({ length: data.length }, (_, i) =>
                      (i + 1).toString()
                    )}
                    onSelect={(selection) => {
                      setIsLoading(true);
                      let selected = [];

                      // Case 1: full row/column selection (e.g., { start: 2, end: 2 })
                      if (
                        typeof selection?.start === "number" &&
                        typeof selection?.end === "number"
                      ) {
                        if (
                          selection?.constructor?.name === "EntireRowsSelection"
                        ) {
                          if (selectionType === "row") {
                            selected = [{ row: selection.start, col: -1 }];
                          } else if (
                            selectionType === "rangeRow" ||
                            selectionType === "both" ||
                            selectionType === "all"
                          ) {
                            for (
                              var i = selection.start;
                              i <= selection.end;
                              i++
                            ) {
                              selected.push({ row: i, col: -1 });
                            }
                          } else {
                            toast.error(
                              `You only allow to select spesific ${selectionType}.`
                            );
                            setSelectedRange("");
                          }
                        } else {
                          if (selectionType === "column") {
                            selected = [{ row: -1, col: selection.start }];
                          } else if (
                            selectionType === "rangeColumn" ||
                            selectionType === "both" ||
                            selectionType === "all"
                          ) {
                            for (
                              var j = selection.start;
                              j <= selection.end;
                              j++
                            ) {
                              selected.push({ row: -1, col: j });
                            }
                          } else {
                            toast.error(
                              `You only allow to select spesific ${selectionType}.`
                            );
                            setSelectedRange("");
                          }
                        }
                      }
                      var error = false;
                      if (selection?.range?.start && selection?.range?.end) {
                        const { start, end } = selection.range;
                        for (let row = start.row; row <= end.row; row++) {
                          for (
                            let col = start.column;
                            col <= end.column;
                            col++
                          ) {
                            if (
                              selectionType === "range" ||
                              selectionType === "all"
                            ) {
                              selected.push({ row, col });
                            } else {
                              error = true;
                              break;
                            }
                          }
                          if (error) {
                            break;
                          }
                        }
                        if (error) {
                          toast.error(
                            `You only allow to select spesific ${selectionType}.`
                          );
                          setSelectedRange("");
                        }
                      }

                      if (selected.length > 0) {
                        const newSelectedRange = getSelectedLabel(selected);
                        setSelectedRange(newSelectedRange);
                      }
                      setIsLoading(false);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div
                className="flex items-center justify-center w-16 h-16 rounded-full"
                style={{ backgroundColor: colors.gray }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-2xl"
                />
              </div>
              <div className="text-center">
                <p
                  className="text-lg font-medium"
                  style={{ color: colors.primary }}
                >
                  No data available
                </p>
                <p className="text-sm mt-1" style={{ color: colors.darkGray }}>
                  The file appears to be empty or could not be processed
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <div className="p-6 border-t border-gray-200 flex justify-end rounded-b-lg bg-white">
          <button
            onClick={() => {
              console.log("Range", selectedRange);
              if (selectedRange && onSubmit) {
                onSubmit(selectedRange);
              }
            }}
            disabled={!selectedRange}
            className="px-6 py-2.5 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[140px] justify-center"
            style={{
              backgroundColor: selectedRange ? colors.primary : colors.darkGray,
              color: colors.white,
              "--tw-ring-color": colors.primary + "40",
            }}
            onMouseEnter={(e) => {
              if (selectedRange) {
                e.target.style.backgroundColor = colors.secondary;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedRange) {
                e.target.style.backgroundColor = colors.primary;
              }
            }}
            type="button"
          >
            <>
              <FontAwesomeIcon icon={faCheck} size="16" />
              Confirm Selection
            </>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileToSpreadsheetModalSelect;
